import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { useRunDeviceCheck, useTaskPolling } from "../hooks";
import type {
  DeviceFinding,
  EstimateJobDetail,
  JobDeviceCheck,
} from "../types";

const SEVERITY_STYLE: Record<
  DeviceFinding["severity"],
  { cls: string; badge: string; label: string }
> = {
  error: {
    cls: "border-red-300 bg-red-50 text-red-900",
    badge: "border-red-400 bg-red-100 text-red-800",
    label: "POWAŻNY",
  },
  warning: {
    cls: "border-amber-300 bg-amber-50 text-amber-900",
    badge: "border-amber-400 bg-amber-100 text-amber-800",
    label: "do sprawdzenia",
  },
  info: {
    cls: "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]",
    badge:
      "border-[var(--color-border)] bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)]",
    label: "informacyjnie",
  },
};

const ACTION_LABELS: Record<string, string> = {
  montaz: "montaż",
  demontaz: "demontaż",
  istniejace: "istniejące",
  przebudowa: "przebudowa",
};

function FindingRow({ f }: { f: DeviceFinding }) {
  const s = SEVERITY_STYLE[f.severity];
  return (
    <div className={`border p-3 text-xs ${s.cls}`}>
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <span className={`px-1.5 py-px text-[9px] font-semibold border ${s.badge}`}>
          {s.label}
        </span>
        <span className="font-semibold text-[13px]">{f.device}</span>
        <span className="text-[11px] opacity-80">— {f.label}</span>
        {f.ordinal != null && (
          <span className="text-[11px] font-mono opacity-80">poz. {f.ordinal}</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mb-2 font-mono text-[10.5px]">
        <div>
          <span className="opacity-70">W wycenie:&nbsp;</span>
          <span className="font-semibold">{f.in_estimate || "—"}</span>
        </div>
        <div>
          <span className="opacity-70">Wg dokumentacji:&nbsp;</span>
          <span className="font-semibold">{f.in_docs || "—"}</span>
        </div>
      </div>
      <p className="leading-relaxed">{f.note}</p>
      {f.quote && <p className="mt-1.5 italic opacity-80">„{f.quote}”</p>}
      {f.recommendation && (
        <p className="mt-1.5 font-medium">→ {f.recommendation}</p>
      )}
    </div>
  );
}

/** Weryfikacja wyceny z DOKUMENTACJĄ projektową.
 *
 *  Przedmiar mówi tylko, co kosztorysant WPISAŁ. Projekt, schematy i rysunki
 *  mówią, co faktycznie trzeba zabudować — i to jedyne miejsce, w którym da się
 *  wyłapać pozycję rozwiniętą z błędnej podstawy (wyceniony SZR zamiast szafki
 *  P3). Panel niczego nie przelicza: pokazuje rozjazdy do decyzji kosztorysanta. */
export function DeviceCheckCard({
  job,
  canEdit,
}: {
  job: EstimateJobDetail;
  canEdit: boolean;
}) {
  const data = job.device_check as JobDeviceCheck;
  const hasData = !!data && Array.isArray(data.devices);
  const devices = hasData ? data.devices : [];
  const findings = hasData ? (data.findings ?? []) : [];
  const errors = findings.filter((f) => f.severity === "error").length;
  const warnings = findings.filter((f) => f.severity === "warning").length;
  const [openRegister, setOpenRegister] = useState(false);
  const run = useRunDeviceCheck();
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const qc = useQueryClient();

  useEffect(() => {
    const s = task.data?.status;
    if (s && ["success", "failure", "cancelled"].includes(s)) {
      if (s === "success") qc.invalidateQueries({ queryKey: ["est", "job", job.id] });
      setTaskId(null);
    }
  }, [task.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rejestr powstał, ale kontrola krzyżowa padła (data.checked === false):
  // 0 rozjazdów znaczy wtedy „nikt nie porównał”, a nie „zgadza się”. Bez tej
  // gałęzi panel pokazywał zielone „Wycena zgodna z dokumentacją” tuż nad
  // ostrzeżeniem, że porównania nie udało się wykonać.
  const unchecked = hasData && devices.length > 0 && !data.checked;
  const badge = !hasData
    ? {
        label: "Nie sprawdzano",
        cls: "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)]",
      }
    : unchecked
      ? {
          label: "Porównania nie wykonano",
          cls: "border-amber-300 bg-amber-50 text-amber-900",
        }
      : devices.length === 0
        ? {
            label: "Brak dokumentacji technicznej",
            cls: "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)]",
          }
        : errors > 0
          ? {
              label: `${errors} poważnych rozjazdów`,
              cls: "border-red-300 bg-red-50 text-red-900",
            }
          : warnings > 0
            ? {
                label: `${warnings} do sprawdzenia`,
                cls: "border-amber-300 bg-amber-50 text-amber-900",
              }
            : {
                label: "Wycena zgodna z dokumentacją",
                cls: "border-emerald-300 bg-emerald-50 text-emerald-900",
              };

  return (
    <div className="card">
      <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">
            Weryfikacja z dokumentacją
          </span>
          <span className={`px-2 py-0.5 text-xs font-medium border ${badge.cls}`}>
            {badge.label}
          </span>
          {hasData && devices.length > 0 && (
            <span className="text-[11px] text-[var(--color-text-muted)]">
              {devices.length} urządzeń z projektu
            </span>
          )}
        </div>
        {canEdit && (
          <Button
            variant="secondary"
            size="sm"
            loading={!!taskId || run.isPending}
            onClick={async () => {
              const r = await run.mutateAsync(job.id);
              setTaskId(r.task_id);
            }}
            title="Przeczytaj opis techniczny, schematy i rysunki jeszcze raz i porównaj wykaz urządzeń z wyceną"
          >
            {taskId
              ? "Sprawdzam…"
              : hasData
                ? "Sprawdź ponownie"
                : "Sprawdź z dokumentacją"}
          </Button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {!hasData && (
          <div className="text-xs text-[var(--color-text-secondary)]">
            Ta wycena powstała, zanim czytaliśmy dokumentację projektową. Kliknij
            „Sprawdź z dokumentacją”, żeby porównać wykaz urządzeń z opisu
            technicznego, schematów i rysunków z tym, co faktycznie zostało
            wycenione.
          </div>
        )}

        {hasData && devices.length === 0 && (
          <div className="text-xs text-[var(--color-text-secondary)]">
            Przeszukano {data.scanned_documents} dokument(ów) — nie znaleziono
            dokumentacji technicznej (opisu, schematów, rysunków). Wycena opiera się
            wyłącznie na przedmiarze, więc błędna podstawa w przedmiarze nie ma jak
            zostać wykryta. Dorzuć projekt do zlecenia i kliknij „Sprawdź ponownie”.
          </div>
        )}

        {hasData && devices.length > 0 && data.summary && (
          <div className="text-xs text-[var(--color-text-secondary)]">
            {data.summary}
          </div>
        )}

        {unchecked && (
          <div className="text-[11px] text-amber-700">
            ⚠ Wykaz urządzeń odczytany, ale porównania z wyceną nie udało się
            wykonać. Kliknij „Sprawdź ponownie”.
          </div>
        )}

        {findings.length > 0 && (
          <div className="space-y-2">
            {findings.map((f, i) => (
              <FindingRow key={i} f={f} />
            ))}
          </div>
        )}

        {devices.length > 0 && (
          <div>
            <button
              className="text-[11px] text-[var(--color-primary)] hover:underline"
              onClick={() => setOpenRegister((o) => !o)}
            >
              {openRegister
                ? "Ukryj wykaz urządzeń z projektu"
                : `Pokaż wykaz urządzeń z projektu (${devices.length})`}
            </button>
            {openRegister && (
              <>
                <p className="mt-2 text-[10px] text-[var(--color-text-muted)]">
                  Odczyt z rysunków i opisu — traktuj jako materiał roboczy do
                  porównania, nie jako pewny wykaz. Przy każdej pozycji jest źródło
                  i dosłowny cytat.
                </p>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                        <th className="font-medium py-1 pr-3">Urządzenie</th>
                        <th className="font-medium py-1 pr-3">Typ</th>
                        <th className="font-medium py-1 pr-3 text-right">Ilość</th>
                        <th className="font-medium py-1 pr-3">Zakres</th>
                        <th className="font-medium py-1">Źródło</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.map((d, i) => (
                        <tr
                          key={i}
                          className="border-b border-[var(--color-border)] align-top"
                        >
                          <td className="py-1 pr-3">
                            {d.name}
                            {d.quote && (
                              <div className="text-[10px] text-[var(--color-text-muted)] italic">
                                „{d.quote}”
                              </div>
                            )}
                          </td>
                          <td className="py-1 pr-3 font-mono">{d.type || "—"}</td>
                          <td className="py-1 pr-3 text-right tabular-nums whitespace-nowrap">
                            {d.quantity ? `${d.quantity} ${d.unit}`.trim() : "—"}
                          </td>
                          <td className="py-1 pr-3">
                            {ACTION_LABELS[d.action] ?? d.action}
                          </td>
                          <td className="py-1 text-[10px] text-[var(--color-text-muted)]">
                            {d.filename}
                            {d.pages.length > 0 ? `, s. ${d.pages.join(", ")}` : ""}
                            {d.source ? ` (${d.source})` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {taskId && (
          <div className="text-[11px] text-[var(--color-text-muted)] whitespace-pre-wrap">
            {task.data?.log?.split("\n").slice(-2).join("\n") || "Uruchamiam…"}
          </div>
        )}
      </div>
    </div>
  );
}
